import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { NotifierService } from "src/app/_services/notifier.service";
import { APIService } from "src/app/api/api.service";

declare let $: any;
declare let window: any;

@Component({
  selector: "app-patching",
  templateUrl: "./patching.component.html",
  styleUrls: ["./patching.component.css"],
})
export class PatchingComponent implements OnInit, OnDestroy {
  urlPrefix: string =
    localStorage.getItem("role") == "Admin" ? "admin" : "client";
  selectedCloudPartner: string = localStorage.getItem("cp-selected");
  accountId: string = localStorage.getItem("accountId");
  regionId: string = localStorage.getItem("regionId");
  currentMessage: Subscription;
  destroyed = false;

  loading = true;
  hasEnvTags = true;
  envTagKeys: string[] = [];
  tagValueList: string[] = [];
  selectedTagValue: string = null;
  instancesList: any[] = [];
  patchDateTime = "";
  toEmailCommunication = "";
  ccEmailCommunication = "";
  showPatchingWindow = false;
  showPatchingHistory = false;
  currentPage = 1;
  itemsPerPage = 10;
  saving = false;

  headers: any[] = [
    { id: "tagName", name: "Host Name" },
    { id: "instanceType", name: "Instance Type" },
    { id: "availabilityZone", name: "Availability Zone" },
    { id: "ipdata", name: "Private IP / Public IP" },
    { id: "instanceStatus", name: "Instance Status", status: true },
  ];

  constructor(
    private apiServer: APIService,
    public notifier: NotifierService
  ) {}

  ngOnInit(): void {
    setTimeout(() => {
      $("#patchingTagValues").selectpicker();
    }, 100);

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      const data = JSON.parse(msg);
      if (data.value == null || data.source != "notifier") {
        return;
      }

      if (data.key == "accountId") {
        if (this.accountId === data.value) {
          return;
        }
        this.accountId = data.value;
        this.bootstrapPage();
      } else if (data.key == "regionId") {
        if (this.regionId === data.value) {
          return;
        }
        this.regionId = data.value;
        this.bootstrapPage();
      }
    });

    this.bootstrapPage();
  }

  async bootstrapPage() {
    this.loading = true;
    this.notifier.loading(true);
    this.hasEnvTags = true;
    this.envTagKeys = [];
    this.tagValueList = [];
    this.selectedTagValue = null;
    this.instancesList = [];
    this.patchDateTime = "";
    this.toEmailCommunication = "";
    this.ccEmailCommunication = "";
    this.showPatchingWindow = false;
    this.showPatchingHistory = false;
    this.currentPage = 1;
    await this.loadEnvTagKeys();
    this.notifier.loading(false);
  }

  async loadEnvTagKeys(nextToken: string = "") {
    const data = {
      action: "listEc2Tags",
      accountId: this.accountId,
      region: this.regionId,
      nextToken,
    };

    const result = await this.postToEc2Api(data);
    if (this.destroyed || !result) {
      return;
    }

    if (result.status == "1" || result.s == "1") {
      const matches = (result.tagKeyList || []).filter((tag: string) =>
        this.isEnvTag(tag)
      );
      this.envTagKeys = [...new Set([...this.envTagKeys, ...matches])];

      if (result.nextToken) {
        await this.loadEnvTagKeys(result.nextToken);
        return;
      }

      this.hasEnvTags = this.envTagKeys.length > 0;
      if (this.hasEnvTags) {
        await this.loadTagValues();
      } else {
        this.loading = false;
        this.notifier.loading(false);
      }
    } else {
      this.loading = false;
      this.hasEnvTags = false;
      this.notifier.loading(false);
      this.notifier.alert(
        "Info",
        "",
        result.error || result.msg || "Unable to load patching data",
        "info",
        5000
      );
    }
  }

  async loadTagValues() {
    const mergedValues = new Set<string>();

    for (const tagKey of this.envTagKeys) {
      const values = await this.fetchValuesForTagKey(tagKey);
      values.forEach((value: string) => {
        if (value) {
          mergedValues.add(value);
        }
      });
    }

    this.tagValueList = Array.from(mergedValues).sort((a, b) =>
      a.localeCompare(b)
    );
    this.selectedTagValue = this.tagValueList[0] || null;

    setTimeout(() => {
      $("#patchingTagValues").selectpicker("refresh");
    }, 100);

    if (this.selectedTagValue) {
      await this.loadInstances();
    } else {
      this.loading = false;
      this.notifier.loading(false);
    }
  }

  async fetchValuesForTagKey(tagKey: string): Promise<string[]> {
    const data = {
      action: "listEc2TagsValues",
      accountId: this.accountId,
      region: this.regionId,
      nextToken: "",
      tagKey,
    };

    const result = await this.postToEc2Api(data);
    if (this.destroyed || !result) {
      return [];
    }

    if (result.status == "1" || result.s == "1") {
      return result.tagKeyList || [];
    }

    return [];
  }

  async loadInstances() {
    if (!this.selectedTagValue) {
      this.instancesList = [];
      return;
    }

    this.loading = true;
    const mergedMap = new Map<string, any>();

    for (const tagKey of this.envTagKeys) {
      const instances = await this.fetchInstancesForTagKey(tagKey);
      instances.forEach((instance: any) => {
        const instanceId = instance.instanceId || instance.InstanceId;
        if (!instanceId) {
          return;
        }
        instance.ipdata =
          (instance.privateIp ? instance.privateIp.toString() : "") +
          (instance.publicIp ? "/" + instance.publicIp.toString() : "");
        instance.actionStart = mergedMap.get(instanceId)?.actionStart || false;
        mergedMap.set(instanceId, instance);
      });
    }

    this.instancesList = Array.from(mergedMap.values());
    this.showPatchingWindow = false;
    this.currentPage = 1;
    this.patchDateTime = "";
    this.toEmailCommunication = "";
    this.ccEmailCommunication = "";
    this.loading = false;
    this.notifier.loading(false);
  }

  async fetchInstancesForTagKey(
    tagKey: string,
    nextToken: string = ""
  ): Promise<any[]> {
    const data: any = {
      action: "testListInstancesBasedOnTagsWithPagination",
      accountId: this.accountId,
      region: this.regionId,
      nextToken,
      tagKey,
      tagValue: this.selectedTagValue,
    };

    const result = await this.postToEc2Api(data);
    if (this.destroyed || !result) {
      return [];
    }

    if (result.status == "1" || result.s == "1") {
      const instances = result.instanceDetails || [];
      if (result.nextToken) {
        const moreInstances: any[] = await this.fetchInstancesForTagKey(
          tagKey,
          result.nextToken
        );
        return [...instances, ...moreInstances];
      }
      return instances;
    }

    return [];
  }

  async postToEc2Api(data: any) {
    const header = {
      Authorization: localStorage.getItem("t"),
    };

    const apiURL = `${APIService.API_ENDPOINTV3}/${this.urlPrefix}/operations/listec2instances`;

    if (window?.analyticsSendEventClick) {
      window.analyticsSendEventClick("Maintenance.Patching", data.action);
    }

    return await this.apiServer.postDataPromis(apiURL, data, header);
  }

  isEnvTag(tag: string) {
    return /^(env|environment)$/i.test((tag || "").trim());
  }

  changeTagValue() {
    this.patchDateTime = "";
    this.toEmailCommunication = "";
    this.ccEmailCommunication = "";
    this.showPatchingWindow = false;
    this.currentPage = 1;
    this.loadInstances();
  }

  getSelectedInstances() {
    return this.instancesList.filter((instance: any) => instance.actionStart);
  }

  goToPatchingWindow() {
    if (this.getSelectedInstances().length === 0) {
      return;
    }
    this.showPatchingWindow = true;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  goBackToSelection() {
    this.showPatchingWindow = false;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  openPatchingHistory() {
    this.showPatchingHistory = true;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  hidePatchingHistory() {
    this.showPatchingHistory = false;
  }

  canSavePatchingWindow() {
    return (
      !!this.patchDateTime &&
      !!this.selectedTagValue &&
      this.parseEmailField(this.toEmailCommunication).length > 0 &&
      this.getSelectedInstances().length > 0
    );
  }

  parseEmailField(value: string) {
    return (value || "")
      .split(",")
      .map((email: string) => email.trim())
      .filter((email: string) => !!email);
  }

  formatPatchingDateTime(value: string) {
    if (!value) {
      return "";
    }
    return value.replace("T", " ");
  }

  async savePatchingWindow() {
    if (!this.canSavePatchingWindow()) {
      return;
    }

    this.saving = true;
    this.notifier.loading(true);

    const hostName = this.getSelectedInstances().map((instance: any) => {
      const host: any = {
        instanceId: instance.instanceId,
      };

      if (instance.tagName) {
        host["instanceName"] = instance.tagName;
      }

      return host;
    });

    const payload = {
      awsAccountId: this.accountId,
      hostDetails: [
        {
          hostName,
          env: this.selectedTagValue,
          nextPatchingDateTime: this.formatPatchingDateTime(this.patchDateTime),
          toEmailCommunication: this.parseEmailField(this.toEmailCommunication),
          ccEmailCommunication: this.parseEmailField(this.ccEmailCommunication),
        },
      ],
    };

    const header = {
      Authorization: localStorage.getItem("t"),
    };

    const apiURL =
      "https://dev.api.swayam.cloud/v3/admin/apis/patching-fetch-save-config";

    const result = await this.apiServer.postDataPromis(apiURL, payload, header);

    if (result?.status === "success") {
      this.notifier.alert(
        "Success",
        "",
        result?.message || "Patching info saved successfully.",
        "success",
        5000
      );
      this.showPatchingWindow = false;
      this.patchDateTime = "";
      this.toEmailCommunication = "";
      this.ccEmailCommunication = "";
      this.instancesList.forEach((instance: any) => {
        instance.actionStart = false;
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      this.notifier.alert(
        "Error",
        "",
        result?.error || result?.msg || "Unable to save patching info.",
        "error",
        5000
      );
    }

    this.saving = false;
    this.notifier.loading(false);
  }

  getStatusClass(status: string) {
    return {
      success: status == "running",
      danger: status == "stopped" || status == "terminated",
      warning: status != "running" && status != "stopped" && status != "terminated",
    };
  }

  trackByInstance(_: number, instance: any) {
    return instance.instanceId;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
  }
}
